import { Link, useParams } from 'react-router-dom';
import { Pencil } from 'lucide-react';
import { useGuide, useDomains } from '@/lib/queries';
import { useAuth } from '@/lib/auth-context';
import MarkdownView from '@/components/MarkdownView';

export default function GuideViewer() {
  const { slug } = useParams<{ slug: string }>();
  const { account, role } = useAuth();
  const q = useGuide(slug);
  const domains = useDomains();

  if (q.isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (q.isError) return <p className="text-sm text-destructive">{(q.error as Error).message}</p>;
  if (!q.data) return null;
  const g = q.data;
  const domain = (domains.data ?? []).find((d) => d.id === g.domainId);
  const canEdit =
    (role === 'owner' || role === 'admin') && g.accountId === account?.id;

  return (
    <article className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div className="space-y-3 min-w-0">
          <p className="font-mono text-[11px] uppercase tracking-[0.22em] text-muted-foreground">
            <Link to="/guides" className="hover:text-foreground">
              Guides
            </Link>{' '}
            {domain ? (
              <>
                <span aria-hidden>·</span>{' '}
                <Link
                  to={`/domains/${domain.slug}`}
                  className="hover:text-foreground"
                >
                  {domain.slug}
                </Link>{' '}
              </>
            ) : null}
            <span aria-hidden>·</span> {g.slug}
          </p>
          <h1
            className="font-display text-3xl md:text-4xl font-semibold tracking-tight"
            data-testid="guide-title"
          >
            {g.title}
          </h1>
          {g.lastReviewed ? (
            <p className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
              Last reviewed {g.lastReviewed}
            </p>
          ) : null}
        </div>
        {canEdit ? (
          <Link
            to={`/guides/${g.slug}/edit`}
            data-testid={`edit-${g.slug}`}
            className="inline-flex items-center gap-1.5 rounded-md border border-rule px-3 py-1.5 text-xs uppercase tracking-wider text-muted-foreground hover:border-primary/40 hover:bg-accent/30 hover:text-foreground transition-colors shrink-0"
          >
            <Pencil className="h-3.5 w-3.5" aria-hidden /> Edit
          </Link>
        ) : null}
      </div>
      <hr className="rule" />
      <MarkdownView body={g.bodyMd} />
    </article>
  );
}
